'use client';
import { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function HeroSection() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1644229041683-f5334a27363f"
          alt="Professional photographer capturing a cinematic portrait in golden hour light with dramatic composition"
          className="w-full h-full object-cover"
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/20" />
      </div>

      {/* Grid Tech Pattern */}
      <div className="absolute inset-0 grid-tech opacity-20 z-0" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Text Content */}
          <div className="lg:col-span-7 space-y-8">
            {/* Rating Pill
            <div className="inline-flex items-center gap-3 px-4 py-2 glass-panel rounded-full animate-fade-up">
              <div className="flex -space-x-2">
                <div className="w-6 h-6 bg-yellow-400 rounded-full border-2 border-background flex items-center justify-center">
                  <Icon name="StarIcon" size={12} className="text-white" variant="solid" />
                </div>
              </div>
              <span className="text-xs font-medium text-white">
                Rated <span className="font-semibold">5.0</span> by 50+ clients
              </span>
            </div> */}

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white leading-tight animate-fade-up delay-100">
              Visual Storytelling
              <br />
              <span className="text-muted-foreground font-light">Through the Lens</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed animate-fade-up delay-200">
              Professional photography capturing life's moments in stunning cinematic detail. From
              weddings to portraits, we create timeless images you'll treasure forever.
            </p>

            {/* Stats
            <div className="flex flex-wrap items-center gap-8 animate-fade-up delay-300">
              <div>
                <p className="text-3xl font-bold text-white">500+</p>
                <p className="text-sm text-muted-foreground">Successful Shoots</p>
              </div>
              <div className="h-10 w-px bg-border" />
              <div>
                <p className="text-3xl font-bold text-white">1M+</p>
                <p className="text-sm text-muted-foreground">Photos Captured</p>
              </div>
              <div className="h-10 w-px bg-border" />
              <div>
                <p className="text-3xl font-bold text-white">98%</p>
                <p className="text-sm text-muted-foreground">Client Satisfaction</p>
              </div>
            </div> */}

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 animate-fade-up delay-400">
              <Link
                href="/gallery"
                className="px-8 py-4 bg-white text-background rounded-lg font-medium hover:bg-white/90 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                View Portfolio
              </Link>
              <Link
                href="#contact"
                className="px-8 py-4 border border-white/20 rounded-lg font-medium text-white hover:bg-white hover:text-background transition-all duration-300"
              >
                Book a Session
              </Link>
            </div>
          </div>

          {/* Right Column: Floating Widgets */}
          <div className="lg:col-span-5 relative h-[500px] animate-fade-scale delay-500">
            {/* Location Badge */}
            <div className="absolute top-0 right-0 glass-panel rounded-full px-4 py-2 flex items-center gap-2 animate-float">
              <Icon name="MapPinIcon" size={16} className="text-white" />
              <span className="text-xs font-semibold text-white">Delaware Based</span>
            </div>

            {/* Stats Widget
            {mounted && (
              <div className="absolute bottom-20 left-0 glass-panel rounded-xl p-6 w-64 animate-float delay-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <Icon name="ChartBarIcon" size={16} className="text-white" />
                    </div>
                    <span className="text-sm font-medium text-white">This Month</span>
                  </div>
                  <span className="text-xs text-success font-mono">+24%</span>
                </div>
                <div className="h-16 flex items-end gap-1">
                  <div className="flex-1 bg-white/20 rounded-t-sm h-[40%]" />
                  <div className="flex-1 bg-white/20 rounded-t-sm h-[55%]" />
                  <div className="flex-1 bg-white/20 rounded-t-sm h-[50%]" />
                  <div className="flex-1 bg-white/30 rounded-t-sm h-[75%]" />
                  <div className="flex-1 bg-blue-500 rounded-t-sm h-[90%] shadow-lg" />
                </div>
              </div>
            )} */}

            {/* Client Testimonial Link
            <div className="absolute bottom-0 right-0 glass-panel rounded-xl p-4 w-72">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-3">
                  <div className="w-10 h-10 rounded-full bg-zinc-300 border-2 border-background flex items-center justify-center text-xs font-bold text-zinc-600">
                    JD
                  </div>
                  <div className="w-10 h-10 rounded-full bg-zinc-400 border-2 border-background flex items-center justify-center text-xs font-bold text-zinc-600">
                    SM
                  </div>
                  <div className="w-10 h-10 rounded-full bg-zinc-200 border-2 border-background flex items-center justify-center text-xs font-bold text-zinc-600">
                    AK
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white flex items-center gap-1">
                    50+ Happy Clients
                    <Icon
                      name="ArrowTopRightOnSquareIcon"
                      size={12}
                      className="text-muted-foreground"
                    />
                  </p>
                  <p className="text-xs text-muted-foreground">Read Reviews</p>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
}
